import type { SwitchProps as MuiSwitchProps } from "@mui/material";
import { Switch as MuiSwitch } from "@mui/material";
import type { FormikValues } from "formik";
import { useField, useFormikContext } from "formik";
import { forwardRef, useCallback, useImperativeHandle } from "react";

import { sleep } from "@citrine/utils/helpers";
import type { Paths } from "@citrine/utils/types";

import { useFormConfiguration } from ".";
import * as sx from "./switch.sx";
import { useValidateOnChange } from "./utils";

export interface SwitchRef {
  setValue: (value: boolean, shouldValidate?: boolean) => void;
}

export interface SwitchProps<Shape extends FormikValues> extends Omit<
  MuiSwitchProps,
  "name" | "value"
> {
  fullWidth?: boolean;
  name: Paths<Shape, boolean>;
  label?: string;
  validateOnChange?: boolean;
}

function SwitchImpl<Shape extends FormikValues>(
  {
    fullWidth = false,
    name,
    onChange,
    key,
    validateOnChange: validateOnChangeProp,
    ...props
  }: SwitchProps<Shape>,
  ref: React.ForwardedRef<SwitchRef>,
) {
  const [field, , helpers] = useField<boolean>(name);
  const { isSubmitting, validateForm } = useFormikContext();
  const { readonly } = useFormConfiguration();

  const validateOnChange = useValidateOnChange(validateOnChangeProp);

  useImperativeHandle(
    ref,
    () => ({
      setValue: helpers.setValue,
    }),
    [helpers.setValue],
  );

  const onChangeCallback = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>, c: boolean) => {
      onChange?.(e, c);
      field.onChange(e);
      if (validateOnChange) {
        // perform this async to allow all form changes to propagate
        await sleep(0);
        validateForm();
      }
    },
    [field, onChange, validateForm, validateOnChange],
  );

  return (
    <sx.FormControlLabel
      fullWidth={fullWidth}
      label={props.label}
      key={key}
      disabled={props.disabled || readonly || isSubmitting}
      control={
        <MuiSwitch
          {...props}
          {...field}
          checked={field.value}
          onChange={onChangeCallback}
        />
      }
    />
  );
}

export const Switch = forwardRef(SwitchImpl) as <
  Shape extends FormikValues = FormikValues,
>(
  props: SwitchProps<Shape> & {
    ref?: React.ForwardedRef<SwitchRef>;
  },
) => ReturnType<typeof SwitchImpl>;
